import { useState } from "react";

import {
  Button,
  Divider,
  Form,
  FormItem,
  FormProps,
  Spacer,
  Textarea,
  TextareaProps,
  noop,
  useColor,
} from "@aircall/tractor";
import { useTranslation } from "react-i18next";

import { PickTranslation, SectionNotesTextAreaProps } from ".";

function pickTranslation({ isFocus }: PickTranslation) {
  if (isFocus) {
    return "note.placeholder_focus";
  }

  return "note.placeholder";
}

export function SectionNotesForm({
  onSubmit,
  onCancel = noop,
  defaultValue = "",
  show = false,
  showSeparator = false,
}: SectionNotesTextAreaProps) {
  const [value, setValue] = useState(defaultValue);
  const [isFocus, setIsFocus] = useState(false);
  const graphicDefaultColor = useColor("graphic-default");

  const hasNoValue = Boolean(!value);

  const { t } = useTranslation();

  const handleOnSubmit: FormProps["onSubmit"] = (e) => {
    e.preventDefault();

    onSubmit?.(e);
    setIsFocus(false);
  };
  const handleOnChange: TextareaProps["onChange"] = (e) => {
    setValue(e.target.value);
  };
  const handleOnClear = () => {
    setValue("");
  };
  const handleOnFocus = () => {
    setIsFocus(true);
  };
  const handleOnBlur: TextareaProps["onBlur"] = (e) => {
    if (e.relatedTarget?.nodeName === "BUTTON") {
      return;
    }

    setIsFocus(false);
  };
  const handleOnCancel = () => {
    onCancel();
    setValue(defaultValue);
    setIsFocus(false);
  };

  if (!show) {
    return null;
  }

  return (
    <Form onSubmit={handleOnSubmit} data-test="notes-form">
      <Spacer space="s" direction="vertical" fluid>
        {showSeparator && show && (
          <Divider w="100%" borderTop={`1px solid ${graphicDefaultColor}`} />
        )}
        <Spacer space="xs" direction="vertical" fluid>
          {/* FormItem name='note' does not work as expected, thus the name attr in Textarea */}
          <FormItem>
            <Textarea
              data-test="notes-form-textarea"
              resizable
              name="content"
              value={value}
              onFocus={handleOnFocus}
              onBlur={handleOnBlur}
              onChange={handleOnChange}
              // Prevents icon display when the value is empty
              onClear={value ? handleOnClear : undefined}
              placeholder={t(pickTranslation({ isFocus }))}
            />
          </FormItem>
          {isFocus && (
            <Spacer
              space="xxs"
              direction="horizontal"
              justifyContent="flex-end"
              fluid
            >
              <FormItem>
                <Button
                  data-test="notes-form-cancel"
                  onClick={handleOnCancel}
                  size="small"
                  variant="secondary"
                  mode="ghost"
                >
                  {t("cancel")}
                </Button>
              </FormItem>
              <FormItem>
                <Button
                  data-test="notes-form-done"
                  size="small"
                  type="submit"
                  disabled={hasNoValue}
                >
                  {t("done")}
                </Button>
              </FormItem>
            </Spacer>
          )}
        </Spacer>
      </Spacer>
    </Form>
  );
}
